import React from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Sidebar.css";
import KapInsigntsLogo from "../assets/img/kapinsightsNewLogo.png";
import ReportingIcon from "../assets/img/ReportingIcon.png";
import BscIcon from "../assets/img/bscIcon.png";
import DocInsightsIcon from "../assets/img/documentInsightsSidebarIcon.png";
import DataCleaningIcon from "../assets/img/dataCleaningSidebarIcon.png";
import DataAnonymisationIcon from "../assets/img/dataAnonymisationSidebarIcon.png";
import ProjectHubIcon from "../assets/img/projectHubSidebarIcon.png";
import LibraryIcon from "../assets/img/librarySidebarIcon.png";
import AuditIcon from "../assets/img/auditSidebarIcon.png";
import CreditsIcon from "../assets/img/coin-credits-tokens.png";
import { CloseOutlined } from "@ant-design/icons";
import { getUserRole } from "../hooks/getUserRoles";
import { normalizeRole, atLeast } from "../utils/roles";

// ─── Tabler-style inline SVG icons ───────────────────────────────────────────
// Using simple inline SVGs so no new image assets are needed.
// Swap these out for your actual icon assets once your designer exports them.

const Icon = ({ d, size = 20 }: { d: string; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    <path d={d} />
  </svg>
);

// PNG icons exported by the designer — rendered at the same footprint as `Icon`.
const ImgIcon = ({ src }: { src: string }) => (
  <img
    src={src}
    alt=""
    style={{ width: 20, height: 20, flexShrink: 0, objectFit: "contain" }}
  />
);

const Icons = {
  translation:
    "M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129",
  dashboard:
    "M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zm10 0a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z",
  tickets:
    "M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z",
  workspaces:
    "M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6",
  users:
    "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z",
  profile:
    "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  subscription:
    "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z",
  surveyGen:
    "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
  formulaBuilder: "M18 4H6l6 8-6 8h12",
};

interface SidebarProps {
  openSidebar: boolean;
  setOpenSidebar: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ openSidebar, setOpenSidebar }) => {
  const { t } = useTranslation("nav");
  const userRole = getUserRole();
  const role = normalizeRole(userRole);
  const location = useLocation();
  const { id: projectId } = useParams();

  const isProjectPath = location.pathname.startsWith("/projects");
  const isProjectDetailsPage =
    location.pathname.includes("/projects/") &&
    location.pathname.includes("/details");

  const isSurveyManagementPage =
    location.pathname.startsWith("/ai-survey-generator") ||
    location.pathname.startsWith("/survey-builder") ||
    location.pathname.startsWith("/upload-survey") ||
    location.pathname.startsWith("/ai-translation") ||
    location.pathname.startsWith("/doc-processing") ||
    location.pathname.startsWith("/formula-builder") ||
    location.pathname.startsWith("/add-ai-translation") ||
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/reporting") ||
    location.pathname.startsWith("/data-cleaning") ||
    location.pathname.startsWith("/data-anonymisation") ||
    location.pathname.startsWith("/project-hub") ||
    location.pathname.startsWith("/library") ||
    location.pathname.startsWith("/upload-file-to-google-drive") ||
    location.pathname.startsWith("/balanced-scorecard") ||
    location.pathname.startsWith("/project-hub") ||
    location.pathname.startsWith("/audit-trails") ||
    location.pathname.startsWith("/dashboard/preview") ||
    location.pathname.startsWith("/upload-file-to-google-drive");

  const navLink =
    (_href: string) =>
    ({ isActive }: { isActive: boolean }) =>
      isActive ? "activeLink" : "";

  return (
    <div className={`sidebar ${!openSidebar ? "closed" : ""}`}>
      <div className="logo-section">
        <a href="/workspaces">
          <img
            src={KapInsigntsLogo}
            alt="KAPinsights"
            style={{ width: "155px", height: "37px" }}
          />
        </a>
        <div className="closeButton" onClick={() => setOpenSidebar(false)}>
          <CloseOutlined />
        </div>
      </div>

      <div className="menu-section">
        {/* ── Admin Management (workspace level) ─────────────────────────── */}
        {!isProjectPath && !isSurveyManagementPage && (
          <>
            <div style={{ marginBottom: "40px" }}>
              <h2>{t("adminManagement")}</h2>
              <ul>
                <li>
                  <NavLink to="/workspaces" className={navLink("/workspaces")}>
                    <Icon d={Icons.workspaces} />
                    {t("workspaces")}
                  </NavLink>
                </li>
                {atLeast(role, "manager") && (
                  <li>
                    <NavLink to="/members" className={navLink("/members")}>
                      <Icon d={Icons.users} />
                      {t("userManagement")}
                    </NavLink>
                  </li>
                )}
              </ul>
            </div>

            <>
              <h2>{t("accountSettings")}</h2>
              <ul>
                <li>
                  <NavLink
                    to="/profile-information"
                    className={navLink("/profile-information")}
                  >
                    <Icon d={Icons.profile} />
                    {t("profileInformation")}
                  </NavLink>
                </li>
                {role === "owner" && (
                  <li>
                    <NavLink
                      to="/subscription-plans"
                      className={navLink("/subscription-plans")}
                    >
                      <Icon d={Icons.subscription} />
                      {t("subscriptionPayment")}
                    </NavLink>
                  </li>
                )}
              </ul>
            </>

            <>
              <h2 style={{ marginTop: "2.5rem" }}>{t("helpCenter")}</h2>
              <ul>
                <li>
                  <NavLink
                    to="/help-center"
                    className={navLink("/help-center")}
                  >
                    <Icon d={Icons.tickets} />
                    {t("tickets")}
                  </NavLink>
                </li>
              </ul>
            </>
          </>
        )}

        {/* ── Project-level nav ───────────────────────────────────────────── */}
        {(isProjectDetailsPage || isSurveyManagementPage) && (
          <>
            {/* BUILD */}
            <h2>{t("build")}</h2>
            <ul>
              {/* AI Survey Generator — kept but will be removed later */}
              {/* {userRole !== "Translation People" &&
                userRole !== "Quality Assurance" && (
                  <li>
                    <NavLink
                      to={`/ai-survey-generator/${projectId}`}
                      className={navLink(`/ai-survey-generator/${projectId}`)}
                    >
                      <Icon d={Icons.surveyGen} />
                      AI Survey Generator
                    </NavLink>
                  </li>
                )} */}

              {/* AI Translation */}
              <li>
                <NavLink
                  to={`/ai-translation/${projectId}`}
                  className={({ isActive }) =>
                    `ai-translation-link ${isActive ? "activeLink" : ""}`
                  }
                >
                  <Icon d={Icons.translation} />
                  {t("aiTranslation")}
                  <img
                    src={CreditsIcon}
                    alt=""
                    style={{
                      width: 26,
                      height: 26,
                      marginLeft: "auto",
                      flexShrink: 0,
                      objectFit: "contain",
                    }}
                  />
                </NavLink>
              </li>

              {/* Project Hub — NEW */}
              <li>
                <NavLink
                  to={`/project-hub/${projectId}`}
                  className={navLink(`/project-hub/${projectId}`)}
                >
                  <ImgIcon src={ProjectHubIcon} />
                  {t("projectHub")}
                </NavLink>
              </li>

              {/* Library — NEW */}
              <li>
                <NavLink
                  to={`/library/${projectId}`}
                  className={navLink(`/library/${projectId}`)}
                >
                  <ImgIcon src={LibraryIcon} />
                  {t("library")}
                </NavLink>
              </li>
            </ul>

            {/* PROCESS */}
            <>
              <h2 style={{ marginTop: "2.5rem" }}>{t("process")}</h2>
              <ul>
                {/* Data Anonymisation — PM/Admin only */}
                {atLeast(role, "manager") && (
                  <li>
                    <NavLink
                      to={`/data-anonymisation/${projectId}`}
                      className={navLink(`/data-anonymisation/${projectId}`)}
                    >
                      <ImgIcon src={DataAnonymisationIcon} />
                      {t("dataAnonymisation")}
                    </NavLink>
                  </li>
                )}

                {/* Data Cleaning — all users */}
                <li>
                  <NavLink
                    to={`/data-cleaning/${projectId}`}
                    className={navLink(`/data-cleaning/${projectId}`)}
                  >
                    <ImgIcon src={DataCleaningIcon} />
                    {t("dataCleaning")}
                  </NavLink>
                </li>
              </ul>
            </>

            {/* ANALYZE — all users */}
            <h2 style={{ marginTop: "2.5rem" }}>{t("analyze")}</h2>
            <ul>
              <li>
                <NavLink
                  to={`/doc-processing/${projectId}`}
                  className={navLink(`/doc-processing/${projectId}`)}
                >
                  <ImgIcon src={DocInsightsIcon} />
                  {t("documentInsights")}
                </NavLink>
              </li>

              <li>
                <NavLink
                  to={`/formula-builder/${projectId}`}
                  className={navLink(`/formula-builder/${projectId}`)}
                >
                  <Icon d={Icons.formulaBuilder} />
                  {t("formulaBuilder")}
                </NavLink>
              </li>
            </ul>
            {/* BALANCED SCORECARD — all users */}
            <>
              <h2 style={{ marginTop: "2.5rem" }}>{t("score")}</h2>
              <ul>
                <li>
                  <NavLink
                    to={`/balanced-scorecard/${projectId}`}
                    className={navLink(`/balanced-scorecard/${projectId}`)}
                  >
                    <ImgIcon src={BscIcon} />
                    {t("balancedScorecard")}
                  </NavLink>
                </li>
              </ul>
            </>
            {/* VISUALIZE — all users */}
            <h2 style={{ marginTop: "2.5rem" }}>{t("visualize")}</h2>
            <ul>
              <li>
                <NavLink
                  to={`/dashboard/${projectId}`}
                  className={navLink(`/dashboard/${projectId}`)}
                >
                  <Icon d={Icons.dashboard} />
                  {t("dashboard")}
                </NavLink>
              </li>
              <li>
                <NavLink
                  to={`/reporting/${projectId}`}
                  className={navLink(`/reporting/${projectId}`)}
                >
                  <ImgIcon src={ReportingIcon} />
                  {t("reporting")}
                </NavLink>
              </li>
            </ul>

            {/* AUDIT — Admin & Project Manager only (API enforces this) */}
            {atLeast(role, "manager") && (
              <>
                <h2 style={{ marginTop: "2.5rem" }}>{t("audit")}</h2>
                <ul>
                  <li>
                    <NavLink
                      to={`/audit-trails/${projectId}`}
                      className={navLink(`/audit-trails/${projectId}`)}
                    >
                      <ImgIcon src={AuditIcon} />
                      {t("auditTrails")}
                    </NavLink>
                  </li>
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
